import { useTemplateStore } from "entity-store/template-store/use-template-store";
import styles from "../admin.module.css";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { usePaths } from "BEDROCK/paths";
import { UIIconButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { useAccount } from "BEDROCK/account/use-account";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { applyTemplateToProject } from "PosterEditor/model/project-template";
import { LinkCTAButton } from "BEDROCK/shared-components/cta-button";
import { ProjectPreview } from "components/common/project-preview";
import { AdminBreadcrumbs } from "admin/collections-admin/admin-breadcrumbs";
import { AlertModal } from "BEDROCK/shared-components/alert-modal";
import { useState } from "react";
import { useDuplicateTemplate } from "admin/hooks/use-duplicate-template";
import { ListItem, ListItemAction } from "admin/shared-components/list-item";

interface TemplateListAdminProps {}

export const TemplateListAdmin = (props: TemplateListAdminProps) => {
  const { account } = useAccount();
  const paths = usePaths();
  const { templates, createTemplate, deleteTemplate, isLoading, isError } =
    useTemplateStore();
  const { draftProject } = useProjectStore();

  const { duplicateTemplate } = useDuplicateTemplate();

  const createProjectWithTemplate = async (template: ProjectTemplate) => {
    const project = await draftProject(false, template);
    applyTemplateToProject(template, project);

    paths.navigateTo(paths.mapEditor + "/" + project.id);
  };

  const editTemplate = (template: ProjectTemplate) => {
    paths.navigateTo(paths.templateEditor + "/" + template.id);
  };

  if (isLoading) {
    return <div>Loading templates...</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <span className={styles.title}>
          <AdminBreadcrumbs items={[{ label: "Templates" }]} />
        </span>
        <ListControls
          onClickAddTemplate={async () => {
            await createTemplate.mutateAsync();
          }}
        />
        <List
          templates={templates ?? []}
          onClick={(template) => {
            createProjectWithTemplate(template);
          }}
          onClickDelete={async (template) => {
            await deleteTemplate.mutateAsync(template);
          }}
          onClickEdit={editTemplate}
          onClickDuplicate={async (template) => {
            await duplicateTemplate.mutateAsync(template);
          }}
        />
      </div>
    </div>
  );
};

interface ListControlsProps {
  onClickAddTemplate: () => void;
}
const ListControls = (props: ListControlsProps) => {
  return (
    <div className={styles.listControls}>
      <div></div>
      <LinkCTAButton onClick={props.onClickAddTemplate}>Create +</LinkCTAButton>
    </div>
  );
};

interface ListProps {
  templates: ProjectTemplate[];
  onClick: (project: ProjectTemplate) => void;
  onClickEdit: (project: ProjectTemplate) => void;
  onClickDelete: (project: ProjectTemplate) => void;
  onClickDuplicate: (template: ProjectTemplate) => void;
}
const List = (props: ListProps) => {
  return (
    <div className={styles.list}>
      {props.templates.map((t) => (
        <Item
          key={t.id}
          template={t}
          onClick={() => {
            props.onClick(t);
          }}
          onClickEdit={() => props.onClickEdit(t)}
          onClickDelete={() => props.onClickDelete(t)}
          onClickDuplicate={() => props.onClickDuplicate(t)}
        />
      ))}
    </div>
  );
};

interface ItemProps {
  template: ProjectTemplate;
  onClick: () => void;
  onClickEdit: () => void;
  onClickDelete: () => void;
  onClickDuplicate: () => void;
}
const Item = (props: ItemProps) => {
  const [isShowingDeleteAlert, setIsShowingDeleteAlert] =
    useState<boolean>(false);
  const { account } = useAccount();

  const onClickDelete = () => {
    setIsShowingDeleteAlert(true);
  };

  return (
    <div className={styles.item}>
      <div className={styles.leadingContent}>
        <button className={styles.itemAction} onClick={props.onClick}>
          <ProjectPreview
            template={props.template}
            size={{ width: 200, height: 200 }}
          />
        </button>
        <div className={styles.textContent}>
          <h1>{props.template.name}</h1>
          <p>This is the template description</p>
        </div>
      </div>
      {account?.permissions === "admin" && (
        <div className={styles.controls}>
          <UIIconButton
            iconName="BIN"
            onClick={onClickDelete}
            width={25}
            height={25}
          />
          <UIIconButton
            iconName="PENCIL"
            onClick={props.onClickEdit}
            width={25}
            height={25}
          />
          <UIIconButton
            iconName="COPY"
            onClick={props.onClickDuplicate}
            width={25}
            height={25}
          />
          <AlertModal
            isShowing={isShowingDeleteAlert}
            title="Delete template?"
            message="This cannot be undone."
            proceedCTA="Delete"
            dismissCTA="Cancel"
            onProceed={props.onClickDelete}
            onDismiss={() => setIsShowingDeleteAlert(false)}
          />
        </div>
      )}
    </div>
  );
};
